<template>
    <pt-popper
        ref="popper"
        v-model:visible="suggestionVisible"
        :placement="placement"
        :popper-class="`pt-autocomplete__popper ${popperClass}`"
        :append-to-body="popperAppendToBody"
        pure
        manual-mode
        effect="light"
        trigger="click"
        transition="pt-zoom-in-top"
        :gpu-acceleration="false"
    >
        <template #trigger>
            <div
                v-clickoutside="close"
                :class="['pt-autocomplete', $attrs.class]"
                :style="$attrs.style"
                role="combobox"
                aria-haspopup="listbox"
                :aria-expanded="suggestionVisible"
                :aria-owns="id"
            >
                <pt-input
                    ref="inputRef"
                    v-bind="ptAttrs"
                    :model-value="modelValue"
                    :disabled="disabled"
                    :placeholder="placeholder"
                    @input="handleInput"
                    @change="handleChange"
                    @focus="handleFocus"
                    @blur="handleBlur"
                    @clear="handleClear"
                    @keydown.up.prevent="highlight(highlightedIndex - 1)"
                    @keydown.down.prevent="highlight(highlightedIndex + 1)"
                    @keydown.enter="handleKeyEnter"
                    @keydown.tab="close"
                >
                    <template v-if="$slots.prepend" #prepend>
                        <slot name="prepend"></slot>
                    </template>
                    <template v-if="$slots.append" #append>
                        <slot name="append"></slot>
                    </template>
                    <template v-if="$slots.prefix" #prefix>
                        <slot name="prefix"></slot>
                    </template>
                    <template v-if="$slots.suffix" #suffix>
                        <slot name="suffix"></slot>
                    </template>
                </pt-input>
            </div>
        </template>
        <template #default>
            <div
                ref="regionRef"
                :class="['pt-autocomplete-suggestion', suggestionLoading && 'is-loading']"
                :style="{ width: dropdownWidth, outline: 'none' }"
                role="region"
            >
                <pt-scrollbar
                    v-show="suggestions && suggestions?.length > 0 && !suggestionLoading"
                    tag="ul"
                    content-class="pt-autocomplete-suggestion__list"
                    :style="{
                        maxHeight: scrollHeight,
                    }"
                >
                    <!-- <li
                        v-if="suggestionLoading"
                        v-loading="suggestionLoading"
                        pt-loading-background="transparent!important"
                    >
                        <i class="pt-icon-loading"></i>
                    </li> -->
                    <!-- <template v-else> -->
                    <li
                        v-for="(item, index) in suggestions"
                        :id="`${id}-item-${index}`"
                        :key="index"
                        :class="{ highlighted: highlightedIndex === index }"
                        role="option"
                        :aria-selected="highlightedIndex === index"
                        @click="select(item)"
                        :title="item[valueKey]"
                    >
                        <slot :item="item">{{ item[valueKey] }}</slot>
                    </li>
                    <!-- </template> -->
                </pt-scrollbar>
                <template v-if="!loading && !suggestionLoading && suggestions?.length <= 0">
                    <slot v-if="$slots.empty" name="empty"></slot>
                    <p v-else class="pt-autocomplete-dropdown__empty">{{ $t('pt.select.noMatch') }}</p>
                </template>
                <template v-if="suggestionLoading">
                    <div
                        v-loading="suggestionLoading"
                        pt-loading-background="transparent!important"
                        class="pt-autocomplete-dropdown__loading"
                    ></div>
                </template>
            </div>
        </template>
    </pt-popper>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUpdated, nextTick, watch } from 'vue';
import usePtAttrs from '../../hooks/use-attrs';
import { NOOP } from '@vue/shared';
import debounce from 'lodash/debounce';
import { ClickOutside } from '../../directives';
import { generateId, isArray } from '../../../utils/util';
import { UPDATE_MODEL_EVENT } from '../../../utils/constants';
import throwError from '../../../utils/error';
import PtInput from '../../input';
import PtScrollbar from '../../scrollbar';
import PtPopper from '../../popper';

import type { PropType } from 'vue';

export default defineComponent({
    name: 'PtAutocomplete',
    components: {
        PtPopper,
        PtInput,
        PtScrollbar,
    },
    directives: {
        clickoutside: ClickOutside,
    },
    inheritAttrs: false,
    props: {
        valueKey: {
            type: String,
            default: 'value',
        },
        modelValue: {
            type: [String, Number],
            default: '',
        },
        debounce: {
            type: Number,
            default: 300,
        },
        placeholder: String,
        placement: {
            type: String,
            validator: (val: string): boolean => {
                return ['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end'].includes(val);
            },
            default: 'bottom-start',
        },
        fetchSuggestions: {
            type: Function as PropType<(queryString: string, cb: (data: any[]) => void) => void>,
            default: NOOP,
        },
        popperClass: {
            type: String,
            default: '',
        },
        triggerOnFocus: {
            type: Boolean,
            default: true,
        },
        selectWhenUnmatched: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideLoading: {
            type: Boolean,
            default: false,
        },
        popperAppendToBody: {
            type: Boolean,
            default: true,
        },
        highlightFirstItem: {
            type: Boolean,
            default: false,
        },
        scrollHeight: {
            type: String,
            default: '180px',
        },
        popperAddWidth: {
            // 弹框额外增加的宽度
            type: Number,
            default: 0,
        },
    },
    emits: [UPDATE_MODEL_EVENT, 'input', 'change', 'focus', 'blur', 'clear', 'select'],
    setup(props, ctx) {
        const ptAttrs = usePtAttrs();
        const suggestions = ref(null);
        const highlightedIndex = ref(-1);
        const dropdownWidth = ref('');
        const activated = ref(false);
        const suggestionDisabled = ref(false);
        const loading = ref(false);
        const inputRef = ref(null);
        const regionRef = ref(null);
        const popper = ref(null);

        const id = computed(() => {
            return `pt-autocomplete-${generateId()}`;
        });
        const suggestionVisible = computed(() => {
            const isValidData = isArray(suggestions.value);
            return (isValidData || loading.value) && activated.value;
        });
        const suggestionLoading = computed(() => {
            return !props.hideLoading && loading.value;
        });

        const updatePopperPosition = () => {
            nextTick(popper.value.update);
        };

        watch(suggestionVisible, () => {
            dropdownWidth.value = `${inputRef.value.$el.offsetWidth + props.popperAddWidth}px`;
        });

        onMounted(() => {
            inputRef.value.inputOrTextarea.setAttribute('role', 'textbox');
            inputRef.value.inputOrTextarea.setAttribute('aria-autocomplete', 'list');
            inputRef.value.inputOrTextarea.setAttribute('aria-controls', 'id');
            inputRef.value.inputOrTextarea.setAttribute(
                'aria-activedescendant',
                `${id.value}-item-${highlightedIndex.value}`,
            );
            const $ul = regionRef.value.querySelector('.pt-autocomplete-suggestion__list');
            $ul.setAttribute('role', 'listbox');
            $ul.setAttribute('id', id.value);
        });

        onUpdated(updatePopperPosition);

        const getData = (queryString) => {
            if (suggestionDisabled.value) {
                return;
            }
            loading.value = true;
            updatePopperPosition();
            props.fetchSuggestions(queryString, (suggestionsArg) => {
                loading.value = false;
                if (suggestionDisabled.value) {
                    return;
                }
                if (isArray(suggestionsArg)) {
                    suggestions.value = suggestionsArg;
                    highlightedIndex.value = props.highlightFirstItem ? 0 : -1;
                } else {
                    throwError('PtAutocomplete', 'autocomplete suggestions must be an array');
                }
            });
        };
        const debouncedGetData = debounce(getData, props.debounce);
        const handleInput = (value) => {
            ctx.emit('input', value);
            ctx.emit(UPDATE_MODEL_EVENT, value);
            suggestionDisabled.value = false;
            if (!props.triggerOnFocus && !value) {
                suggestionDisabled.value = true;
                suggestions.value = null;
                return;
            }
            debouncedGetData(value);
        };
        const handleChange = (value) => {
            ctx.emit('change', value);
        };
        const handleFocus = (e) => {
            activated.value = true;
            ctx.emit('focus', e);
            if (props.triggerOnFocus) {
                debouncedGetData(props.modelValue);
            }
        };
        const handleBlur = (e) => {
            ctx.emit('blur', e);
        };
        const handleClear = () => {
            activated.value = false;
            ctx.emit(UPDATE_MODEL_EVENT, '');
            ctx.emit('clear');
            inputRef.value?.input?.blur(); // 此处失去焦点处理是为了 再次focus时 suggestion可以出现
            inputRef.value?.inputOrTextarea?.blur(); // 此处失去焦点处理是为了 再次focus时 suggestion可以出现
        };
        const handleKeyEnter = () => {
            if (
                suggestionVisible.value &&
                highlightedIndex.value >= 0 &&
                highlightedIndex.value < suggestions.value.length
            ) {
                select(suggestions.value[highlightedIndex.value]);
            } else if (props.selectWhenUnmatched) {
                ctx.emit('select', { value: props.modelValue });
                nextTick(() => {
                    suggestions.value = null;
                    highlightedIndex.value = -1;
                });
            }
        };
        const close = () => {
            activated.value = false;
        };
        const focus = () => {
            inputRef.value.focus();
        };
        const select = (item) => {
            ctx.emit('input', item[props.valueKey]);
            ctx.emit(UPDATE_MODEL_EVENT, item[props.valueKey]);
            ctx.emit('select', item);
            nextTick(() => {
                suggestions.value = null;
                highlightedIndex.value = -1;
                activated.value = false;
            });
        };
        const highlight = (index) => {
            if (!suggestionVisible.value || loading.value) {
                return;
            }
            if (index < 0) {
                highlightedIndex.value = -1;
                return;
            }
            if (index >= suggestions.value.length) {
                index = suggestions.value.length - 1;
            }
            const suggestion = regionRef.value.querySelector('.pt-autocomplete-suggestion__wrap');
            const suggestionList = suggestion.querySelectorAll('.pt-autocomplete-suggestion__list li');
            const highlightItem = suggestionList[index];
            const scrollTop = suggestion.scrollTop;
            const offsetTop = highlightItem.offsetTop;

            if (offsetTop + highlightItem.scrollHeight > scrollTop + suggestion.clientHeight) {
                suggestion.scrollTop += highlightItem.scrollHeight;
            }
            if (offsetTop < scrollTop) {
                suggestion.scrollTop -= highlightItem.scrollHeight;
            }
            highlightedIndex.value = index;
            inputRef.value.inputOrTextarea.setAttribute(
                'aria-activedescendant',
                `${id.value}-item-${highlightedIndex.value}`,
            );
        };

        return {
            ptAttrs,
            suggestions,
            highlightedIndex,
            dropdownWidth,
            activated,
            suggestionDisabled,
            loading,
            inputRef,
            regionRef,
            popper,

            id,
            suggestionVisible,
            suggestionLoading,
            scrollHeight: props.scrollHeight,
            getData,
            handleInput,
            handleChange,
            handleFocus,
            handleBlur,
            handleClear,
            handleKeyEnter,
            close,
            focus,
            select,
            highlight,
        };
    },
});
</script>

<style lang="scss" src="../style/autocomplete.scss"></style>
